/**
 * Résolution des slots de rotation d'un forfait pour une date de livraison.
 *
 * Un forfait reste une entité avec un listing d'items, mais ce listing peut
 * contenir des lignes de slot (package.slot_lines) : « ce forfait contient
 * <quantity> × <slot> ». Le produit concret est donné par le calendrier du
 * slot (entries) : l'entrée valable = la dernière dont effective_from <= date.
 *
 * La composition effective = lignes fixes (products / product_parts)
 * + lignes de slot résolues. Le devis stocke toujours des produits concrets.
 */

export function normalizeDateYmd(value) {
  if (!value) {
    return null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const match = String(value).match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

const sortedEntries = (slot) =>
  [...(slot.entries || [])].sort((a, b) => {
    const cmp = String(a.effective_from || "").localeCompare(String(b.effective_from || ""));
    return cmp !== 0 ? cmp : (a.id || 0) - (b.id || 0);
  });

const entryCovers = (entry, d) => {
  const from = normalizeDateYmd(entry.effective_from);
  if (!from || d < from) return false;
  const until = normalizeDateYmd(entry.effective_until);
  return !until || d <= until;
};

const shiftYear = (d, delta) => {
  const [y, rest] = [d.slice(0, 4), d.slice(4)];
  return `${Number(y) + delta}${rest}`;
};

/**
 * La rotation valable à une date, avec le mode de résolution.
 * Miroir de RotationSlot::resolveEntryWithModeOn côté API :
 * 1. rotation qui couvre la date (mode "exact") ;
 * 2. sinon celle qui couvrait la même date l'année d'avant ("previous_year") ;
 * 3. sinon prolongation de la dernière rotation échue ("extended").
 */
export function resolveSlotEntryWithModeOn(slot, dateYmd) {
  const d = normalizeDateYmd(dateYmd);
  if (!slot || !d || !Array.isArray(slot.entries)) {
    return { entry: null, mode: null };
  }
  const entries = sortedEntries(slot);

  let resolved = null;
  entries.forEach((entry) => {
    if (entryCovers(entry, d)) resolved = entry;
  });
  if (resolved) return { entry: resolved, mode: "exact" };

  const previousYear = shiftYear(d, -1);
  entries.forEach((entry) => {
    if (entryCovers(entry, previousYear)) resolved = entry;
  });
  if (resolved) return { entry: resolved, mode: "previous_year" };

  entries.forEach((entry) => {
    const from = normalizeDateYmd(entry.effective_from);
    if (from && from <= d) resolved = entry;
  });
  return resolved ? { entry: resolved, mode: "extended" } : { entry: null, mode: null };
}

/**
 * La rotation valable à une date (fallback inclus), ou null.
 */
export function resolveSlotEntryOn(slot, dateYmd) {
  return resolveSlotEntryWithModeOn(slot, dateYmd).entry;
}

export const RESOLUTION_MODE_LABELS = {
  previous_year: "année N-1",
  extended: "prolongé",
};

/**
 * Coût matière (centimes) d'un forfait ou groupe à partir de sa composition
 * RÉSOLUE (products / product_parts, lignes de slot déjà fusionnées) :
 * prix d'achat saisi sur la fiche en priorité, sinon somme des
 * `purchase_price_ht` des produits composants (part = prix du produit parent
 * ÷ parts_per_product). Retourne { cost, complete, manual } ou null si aucun
 * composant n'a de prix d'achat.
 *
 * @param {object} ref offre résolue (resolvePackageForDeliveryDate / resolveGroupForDeliveryDate)
 * @param {object} productsById map id → produit (adminData.products)
 * @param {object} partsById map id → part (adminData.productParts)
 */
export function computeResolvedCost(ref, productsById, partsById) {
  if (!ref) {
    return null;
  }
  if (ref.purchase_price_ht !== null && ref.purchase_price_ht !== undefined) {
    return { cost: Number(ref.purchase_price_ht), complete: true, manual: true };
  }
  let cost = 0;
  let hasAny = false;
  let complete = true;
  (ref.products || []).forEach((line) => {
    const p = productsById[line.product_id];
    const v = p?.purchase_price_ht;
    if (v === null || v === undefined) {
      complete = false;
      return;
    }
    cost += Number(v) * (Number(line.quantity) || 1);
    hasAny = true;
  });
  (ref.product_parts || []).forEach((line) => {
    const part = partsById[line.product_part_id];
    const parent = part ? productsById[part.product_id] : null;
    const v = parent?.purchase_price_ht;
    const ppp = Number(line.parts_per_product || part?.parts_per_product) || 1;
    if (v === null || v === undefined) {
      complete = false;
      return;
    }
    cost += Math.round(Number(v) / ppp) * (Number(line.quantity) || 1);
    hasAny = true;
  });
  return hasAny ? { cost: Math.round(cost), complete, manual: false } : null;
}

/**
 * Retourne le groupe (plateau-repas) avec ses lignes de slot résolues et
 * fusionnées dans products. Même contrat que resolvePackageForDeliveryDate
 * (slots de type produit uniquement côté groupes).
 *
 * @param {object} productGroup groupe API (products / slot_lines)
 * @param {string|Date|null|undefined} deliveryDate date de livraison
 * @param {Array} slots liste des slots API (state.rotationSlots.data)
 */
export function resolveGroupForDeliveryDate(productGroup, deliveryDate, slots) {
  if (!productGroup || !Array.isArray(productGroup.slot_lines) || !productGroup.slot_lines.length) {
    return productGroup;
  }
  const d = normalizeDateYmd(deliveryDate);
  const slotsById = {};
  (slots || []).forEach((slot) => {
    slotsById[slot.id] = slot;
  });

  const products = (productGroup.products || []).map((p) => ({ ...p }));
  const unresolved = [];

  productGroup.slot_lines.forEach((line) => {
    const slot = slotsById[line.slot_id];
    const { entry, mode } = d && slot ? resolveSlotEntryWithModeOn(slot, d) : { entry: null, mode: null };
    if (!entry || !entry.item_id) {
      unresolved.push({
        slot_id: line.slot_id,
        slot_name: line.slot_name || slot?.name || `Slot #${line.slot_id}`,
        reason: !slot ? "slot_inconnu" : !d ? "pas_de_date" : "pas_d_entree_a_cette_date",
      });
      return;
    }
    products.push({
      quantity: line.quantity,
      product_id: entry.item_id,
      name: entry.name,
      sweet: typeof entry.sweet === "boolean" ? entry.sweet : undefined,
      _slot_id: line.slot_id,
      _slot_name: line.slot_name || slot.name,
      _resolution_mode: mode,
    });
  });

  return {
    ...productGroup,
    products,
    _unresolvedSlots: unresolved,
  };
}

/**
 * Retourne le forfait avec les lignes de slot résolues et fusionnées dans
 * products / product_parts. Les slots non résolubles (pas d'entrée à la date,
 * pas de date, slot inconnu) sont listés dans _unresolvedSlots.
 *
 * @param {object} productPackage forfait API (products / product_parts / slot_lines)
 * @param {string|Date|null|undefined} deliveryDate date de livraison
 * @param {Array} slots liste des slots API (state.rotationSlots.data)
 */
export function resolvePackageForDeliveryDate(productPackage, deliveryDate, slots) {
  if (!productPackage || !Array.isArray(productPackage.slot_lines) || !productPackage.slot_lines.length) {
    return productPackage;
  }

  const d = normalizeDateYmd(deliveryDate);
  const slotsById = {};
  (slots || []).forEach((slot) => {
    slotsById[slot.id] = slot;
  });

  const products = (productPackage.products || []).map((p) => ({ ...p }));
  const productParts = (productPackage.product_parts || []).map((p) => ({ ...p }));
  const unresolved = [];

  productPackage.slot_lines.forEach((line) => {
    const slot = slotsById[line.slot_id];
    const { entry, mode } = d && slot ? resolveSlotEntryWithModeOn(slot, d) : { entry: null, mode: null };
    if (!entry || !entry.item_id) {
      unresolved.push({
        slot_id: line.slot_id,
        slot_name: line.slot_name || slot?.name || `Slot #${line.slot_id}`,
        reason: !slot ? "slot_inconnu" : !d ? "pas_de_date" : "pas_d_entree_a_cette_date",
      });
      return;
    }
    const resolvedLine = {
      quantity: line.quantity,
      name: entry.name,
      sweet: typeof entry.sweet === "boolean" ? entry.sweet : undefined,
      _slot_id: line.slot_id,
      _slot_name: line.slot_name || slot.name,
      _resolution_mode: mode,
    };
    if ((line.item_type || slot.item_type) === "product_part") {
      productParts.push({
        ...resolvedLine,
        product_part_id: entry.item_id,
        parts_per_product: entry.parts_per_product,
      });
    } else {
      products.push({ ...resolvedLine, product_id: entry.item_id });
    }
  });

  return {
    ...productPackage,
    products,
    product_parts: productParts,
    _unresolvedSlots: unresolved,
  };
}
